import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Header from "../../components/header";
import Footer from "../../components/footer";
import personalImage from "../../images/current-account/personal.jpg";
import businessImage from "../../images/current-account/business.jpg";
import salaryImage from "../../images/current-account/salary.jpg";
import heroPattern from "../../images/current-account/hero-pattern.svg";
import checkIcon from "../../images/current-account/check.svg";
import styles from "./index.module.css";

const accountUrl = "https://zikoramfb.qoreonline.com/onboarding/type";
const appUrl = "https://onelink.to/zikora";

const accounts = [
  {
    id: "personal",
    shortTitle: "Personal",
    title: "Zikora MFB Personal",
    image: personalImage,
    imageAlt: "Customer using her phone at home",
    description: "Break down all your banking needs with a simple readily accessible account that gives you access to unlimited withdrawals, unlimited deposits and easy spending capabilities.",
    benefits: [
      "Access to 24/7, all year round banking services.",
      "Personalized bank card with a flexible limit determined by you",
      "Access to a chequebook to make transactions smoother",
      "Unlimited access to the Zikora MFB Digital Banking Suite",
    ],
    requirements: [
      "Signed copy of Zikora MFB account opening form",
      "Valid identification (Passport, Driving license etc)",
      "2 duly signed & completed reference forms",
      "Account opening form & One passport photograph",
      "3 Months utility bill with current address",
      "BVN",
    ],
  },
  {
    id: "business",
    shortTitle: "Business Banking",
    title: "Zikora MFB Business Banking",
    image: businessImage,
    imageAlt: "Produce and groceries in a small business",
    description: "Whether you are starting a business or have an operational business that is ready to expand, Zikora MFB has a team of financial advisors and banking tools to help you achieve your goals.",
    benefits: [
      "Access to unlimited withdrawals",
      "Access to Zikora MFB financial advisors",
    ],
    requirements: [
      "Minimum opening balance of N5,000",
      "Valid identification (Passport, Driving license etc)",
      "2 duly signed & completed reference forms",
      "Board Resolution",
      "Account opening form & One passport photograph",
      "3 Months utility bill with current address",
      "CAC Document",
      "Tin Number",
    ],
  },
  {
    id: "salary",
    shortTitle: "Salary",
    title: "Zikora MFB Salary",
    image: salaryImage,
    imageAlt: "Professional working at a laptop",
    description: "We provide accounts with unlimited access to our banking facilities tailored to employees.",
    benefits: [
      "Access to unlimited withdrawals",
      "Access to Zikora MFB financial advisors",
    ],
    requirements: [
      "Signed copy of Zikora MFB account opening form",
      "Valid identification (Passport, Driving license etc)",
      "2 duly signed & completed reference forms",
      "Account opening form & One passport photograph",
      "3 Months utility bill with current address",
    ],
  },
];

const Label = ({ children, light = false }) => (
  <div className={styles.label + (light ? " " + styles.labelLight : "")}>
    <span aria-hidden="true" />{children}
  </div>
);

const Actions = ({ compact = false }) => (
  <div className={styles.actions + (compact ? " " + styles.compactActions : "")}>
    <a className={styles.primaryButton} href={accountUrl} target="_blank" rel="noopener noreferrer">Open Account</a>
    <a className={styles.secondaryButton} href={appUrl} target="_blank" rel="noopener noreferrer">Download the App</a>
  </div>
);

const RequirementList = ({ requirements }) => {
  const split = Math.ceil(requirements.length / 2);
  return <div className={styles.requirements}>
    <div className={styles.requirementsLabel}>Requirements</div>
    <div className={styles.requirementColumns}>
      {[requirements.slice(0, split), requirements.slice(split)].map((column, columnIndex) => (
        <ul key={columnIndex}>{column.map((item) => <li key={item}><img src={checkIcon} alt="" />{item}</li>)}</ul>
      ))}
    </div>
  </div>;
};

const AccountArticle = ({ account, index, reducedMotion }) => <motion.article
  className={styles.accountArticle}
  id={account.id}
  initial={reducedMotion ? false : { opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true, amount: 0.12 }}
  transition={{ duration: reducedMotion ? 0 : 0.65, ease: [0.22, 1, 0.36, 1] }}
>
  <img className={styles.accountImage} src={account.image} alt={account.imageAlt} loading="lazy" decoding="async" />
  <div className={styles.accountDetails}>
    <div className={styles.accountIntro}>
      <div><span className={styles.accountNumber}>0{index + 1}</span><h2>{account.title}</h2></div>
      <p>{account.description}</p>
    </div>
    <ul className={styles.benefits}>{account.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}</ul>
    <RequirementList requirements={account.requirements} />
  </div>
</motion.article>;

const CurrentAccount = () => {
  const [activeAccount, setActiveAccount] = useState(0);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const sections = accounts.map(({ id }) => document.getElementById(id)).filter(Boolean);
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      if (visible.length) setActiveAccount(accounts.findIndex(({ id }) => id === visible[0].target.id));
    }, { rootMargin: "-15% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75] });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return <main className={styles.page}>
    <Header solid compactMobile />
    <section className={styles.hero} style={{ "--hero-pattern": "url(" + heroPattern + ")" }}>
      <div className={styles.heroContent}>
        <Label light>Accounts</Label>
        <h1>Current <em>Account</em></h1>
        <p>We provide different current accounts to give you access to overdraft facilities and customer finance for urgent business needs.</p>
        <Actions />
      </div>
    </section>

    <section className={styles.accountsSection} aria-label="Three current accounts">
      <div className={styles.accountsLayout}>
        <aside className={styles.accountIndex}>
          <Label>Three current accounts</Label>
          <nav aria-label="Current account types">
            {accounts.map((account, index) => <a href={"#" + account.id} key={account.id} className={index === activeAccount ? styles.activeIndex : ""} aria-current={index === activeAccount ? "location" : undefined}>
              <span>0{index + 1}</span>{account.shortTitle}
            </a>)}
          </nav>
        </aside>
        <div className={styles.accountList}>
          <div className={styles.mobileSectionLabel}><Label>Three current accounts</Label></div>
          {accounts.map((account, index) => <AccountArticle key={account.id} account={account} index={index} reducedMotion={reducedMotion} />)}
        </div>
      </div>
    </section>

    <section className={styles.ctaSection}>
      <div className={styles.ctaCard}>
        <Label light>Get started</Label>
        <h2>Ready to get started with smarter banking?</h2>
        <p>Open your account in minutes or download the Zikora App to bank anytime, anywhere.</p>
        <Actions compact />
      </div>
    </section>
    <div className={styles.footer}><Footer about /></div>
  </main>;
};

export default CurrentAccount;
